import { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { categories, articles } from '@/mocks/articles';

export default function Category() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const [activeSlug, setActiveSlug] = useState(slug || categories[0].slug);

  useEffect(() => {
    if (slug) {
      setActiveSlug(slug);
    }
  }, [slug]);

  const activeCategory = categories.find((c) => c.slug === activeSlug) || categories[0];
  const filteredArticles = articles.filter((a) => a.category === activeSlug);

  const handleCategoryChange = (newSlug: string) => {
    setActiveSlug(newSlug);
    navigate(`/category/${newSlug}`);
    window.scrollTo({ top: 0, behavior: 'instant' });
  };

  return (
    <>
      <ScrollToTop />
      <Navbar />

      <main>
        <Breadcrumb
          items={[
            { label: '首页', to: '/' },
            { label: activeCategory.name },
          ]}
        />

        {/* Category Header */}
        <section className="py-10 md:py-14 bg-background-50">
          <div className="w-full max-w-7xl mx-auto px-4 md:px-6">
            <div className="flex items-center gap-4 mb-2">
              <div className="w-12 h-12 flex items-center justify-center bg-primary-100 rounded-lg">
                <i className={`${activeCategory.icon} text-primary-600 text-xl`}></i>
              </div>
              <div>
                <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900">
                  {activeCategory.name}
                </h1>
                <p className="text-sm text-foreground-600 mt-1">{activeCategory.description}</p>
              </div>
            </div>
          </div>
        </section>

        {/* Category Tabs */}
        <section className="border-b border-background-200/70 bg-background-50 sticky top-16 z-40">
          <div className="w-full max-w-7xl mx-auto px-4 md:px-6">
            <div className="flex items-center gap-1 overflow-x-auto py-3 scrollbar-hide">
              {categories.map((cat) => (
                <button
                  key={cat.slug}
                  onClick={() => handleCategoryChange(cat.slug)}
                  className={`px-4 py-2 text-sm font-medium rounded-full transition-all duration-200 cursor-pointer whitespace-nowrap ${
                    activeSlug === cat.slug
                      ? 'bg-primary-500 text-background-50'
                      : 'text-foreground-600 hover:text-foreground-900 hover:bg-background-100'
                  }`}
                >
                  {cat.name}
                </button>
              ))}
            </div>
          </div>
        </section>

        {/* Articles List */}
        <section className="py-10 md:py-14 bg-background-50">
          <div className="w-full max-w-7xl mx-auto px-4 md:px-6">
            {filteredArticles.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {filteredArticles.map((article) => (
                  <Link
                    key={article.id}
                    to={`/article/${article.slug}`}
                    className="group bg-background-50 border border-background-200/70 rounded-lg overflow-hidden hover:border-primary-200 transition-all duration-300 cursor-pointer"
                  >
                    <div className="relative w-full h-44 overflow-hidden">
                      <img
                        src={article.image}
                        alt={article.title}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    <div className="p-5">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="text-xs text-accent-600 bg-accent-100 px-2 py-0.5 rounded-full">
                          {article.readTime}
                        </span>
                        <span className="text-xs text-foreground-500">{article.date}</span>
                      </div>
                      <h2 className="font-heading text-base font-semibold text-foreground-900 mb-2 line-clamp-2 group-hover:text-primary-600 transition-colors duration-200">
                        {article.title}
                      </h2>
                      <p className="text-sm text-foreground-600 line-clamp-2 leading-relaxed">
                        {article.excerpt}
                      </p>
                      <div className="flex flex-wrap gap-1.5 mt-3">
                        {article.tags.map((tag) => (
                          <span
                            key={tag}
                            className="text-xs text-foreground-500 bg-background-100 px-2 py-0.5 rounded"
                          >
                            {tag}
                          </span>
                        ))}
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            ) : (
              <div className="text-center py-20">
                <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center bg-background-100 rounded-full">
                  <i className="ri-file-search-line text-foreground-400 text-2xl"></i>
                </div>
                <p className="text-foreground-600 text-sm">该分类暂无文章，请查看其他分类</p>
              </div>
            )}
          </div>
        </section>
      </main>

      <Footer />

      {/* Schema.org */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'CollectionPage',
            name: `${activeCategory.name} - 奸情调查取证全攻略`,
            description: activeCategory.description,
            isPartOf: {
              '@type': 'WebSite',
              name: '奸情调查取证全攻略',
              url: 'https://marriagelaw.github.io/',
            },
            breadcrumb: {
              '@type': 'BreadcrumbList',
              itemListElement: [
                { '@type': 'ListItem', position: 1, name: '首页', item: 'https://marriagelaw.github.io/' },
                { '@type': 'ListItem', position: 2, name: activeCategory.name },
              ],
            },
          }),
        }}
      />
    </>
  );
}